<template>
  <a-form
    class="flex flex-col gap-4 p-8 transition-all duration-300 bg-white rounded-2xl"
    :model="formState"
    :name="formName"
    autocomplete="off"
    layout="vertical"
    @finish="onFinish"
    @finishFailed="onFinishFailed"
  >
    <a-divider orientation="left" orientation-margin="0px" class="!m-0">
      <span class="text-4xl font-semibold">ลงทะเบียน</span>
    </a-divider>
    <a-row :gutter="[16, 12]">
      <a-col :span="6">
        <PrefixName
          label="คำนำหน้า"
          name="prefix"
          v-model:value="formState.prefix"
          v-model:id="formState.title"
          required
        />
      </a-col>
      <a-col :span="9">
        <BaseInput
          label="ชื่อ"
          name="firstName"
          v-model="formState.firstName"
          required
        />
      </a-col>
      <a-col :span="9">
        <BaseInput
          label="นามสกุล"
          name="lastName"
          v-model="formState.lastName"
          required
        />
      </a-col>
      <a-col :span="12">
        <BaseInput
          label="หมายเลขบัตรประชาชน"
          name="identityCard"
          v-model="formState.identityCard"
          type="number"
          :maxLength="13"
          :rules="[{ validator: validateIdentityCard }]"
          disabled
        />
      </a-col>
      <a-col :span="12">
        <BaseDatePicker2
          name="birthDate"
          label="วัน/เดือน/ปีเกิด"
          v-model="formState.birthDate"
          required
        />
      </a-col>
    </a-row>
    <hr class="mt-4 mb-4" />
    <a-row :gutter="[16, 12]">
      <a-col :span="12">
        <BaseInput
          label="อีเมล"
          name="email"
          v-model="formState.email"
          :rules="[{ type: 'email', message: '' }]"
          autocomplete="off"
          disabled
        />
      </a-col>
      <a-col :span="12"></a-col>
      <a-col :span="12">
        <BaseInput
          label="รหัสผ่าน"
          name="password"
          type="password"
          v-model="formState.password"
          :rules="[{ validator: validatePassword1 }]"
          :maxLength="15"
          autocomplete="new-password"
          required
        />
      </a-col>
      <a-col :span="12">
        <BaseInput
          label="ยืนยันรหัสผ่าน"
          name="password2"
          type="password"
          v-model="formState.password2"
          :maxLength="15"
          autocomplete="new-password"
          required
        />
      </a-col>
      <PasswordChecker :password="formState.password" />
    </a-row>
    <hr class="mt-auto mb-4" />
    <div class="flex justify-center gap-6">
      <a-button
        class="w-40"
        html-type="button"
        @click="onCancel"
        :disabled="isLoading"
      >
        ย้อนกลับ
      </a-button>
      <a-button
        type="primary"
        class="w-40"
        html-type="submit"
        :disabled="isLoading"
      >
        <a-spin v-if="isLoading" class="mt-[10px]" />
        <span v-else>บันทึก</span>
      </a-button>
    </div>
  </a-form>
</template>

<script setup lang="ts">
import { fetchUserProfile } from "@/apis/register";
import type { PostUserProfileRequest } from "@/apis/register/post/type";
import PasswordChecker from "@/components/account/PasswordChecker.vue";
import BaseDatePicker2 from "@/components/formControls/BaseDatePicker2.vue";
import BaseInput from "@/components/formControls/BaseInput.vue";
import PrefixName from "@/components/prefixName/PrefixName.vue";
import { useLoading } from "@/composables/useLoading";
import { regexPassword } from "@/configs/other";
import { ROUTE_NAMES } from "@/router/constants";
import { debugConsole, validateIdentityCard } from "@/utils/helper";
import { message } from "ant-design-vue";
import _ from "lodash";
import { onMounted, reactive } from "vue";
import { useRouter } from "vue-router";
import type { UserProfileFormState } from "./type";

const formName = "profile-form";
const emit = defineEmits(["update:step"]);
const router = useRouter();
const { isLoading, disableButton, enableButton } = useLoading();

const formState = reactive<UserProfileFormState>({
  title: undefined,
  prefix: undefined,
  firstName: undefined,
  lastName: undefined,
  identityCard: undefined,
  birthDate: undefined,
  email: undefined,
  password: undefined,
  password2: undefined,
});

onMounted(async () => {
  const { status, data } = await fetchUserProfile.get();
  if (status === 200 && !_.isEmpty(data)) {
    formState.title = data.title;
    formState.prefix = data.prefix;
    formState.firstName = data.firstName;
    formState.lastName = data.lastName;
    formState.identityCard = data.identityCard;
    formState.birthDate = data.birthDate;
    formState.email = data.email;
  }
});

async function validatePassword1(_rule: any, value: any) {
  if (
    !regexPassword.lowercase.test(value) ||
    !regexPassword.uppercase.test(value) ||
    !regexPassword.digital.test(value) ||
    value.length < 8
  ) {
    return Promise.reject("");
  } else {
    return Promise.resolve();
  }
}

const onFinish = async (values: UserProfileFormState) => {
  debugConsole(values, `${formName} success`);
  disableButton();
  if (values.password !== values.password2) {
    message.warning("กรุณากรอกรหัสผ่านให้ตรงกันทั้งสองช่อง");
    formState.password = "";
    formState.password2 = "";
  } else {
    const request: PostUserProfileRequest = {
      title: formState.title,
      prefix: values.prefix,
      firstName: values.firstName,
      lastName: values.lastName,
      identityCard: values.identityCard,
      birthDate: values.birthDate?.toISOString(),
      email: values.email,
      password: values.password,
    };
    const { status, statusText } = await fetchUserProfile.post(request);
    if (status === 201) {
      message.success(statusText);
      router.push({ name: ROUTE_NAMES.HOME });
    } else {
      message.warn(statusText);
    }
  }
  enableButton();
};

function onFinishFailed(errorInfo: any) {
  debugConsole(errorInfo, `${formName} failed`);
  message.warn("ผู้ใช้กรอกแบบฟอร์มไม่สมบูรณ์");
}

function onCancel() {
  router.push({ name: ROUTE_NAMES.HOME });
}
</script>
